import { useEffect, useState, useRef } from "react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { PaperPlaneTilt, CheckCircle, ArrowsClockwise, ClockCountdown, PencilSimple, Plus, Trash, FloppyDisk, ListBullets, ChartBar, Paperclip, DownloadSimple } from "@phosphor-icons/react";
import { DEPARTMENTS } from "@/pages/Workflow";

const ROLES = [
  { value: "admin", label: "Administrator" }, { value: "vanzari", label: "Vânzări" },
  { value: "asistent_vanzari", label: "Asistent Vânzări" }, { value: "pm", label: "Manager Proiect" },
  { value: "inginer", label: "Inginer" },
];
const STATUS_META = {
  pending: { label: "În așteptare", cls: "bg-white/5 text-muted-foreground", bar: "bg-slate-500" },
  in_progress: { label: "În lucru", cls: "bg-blue-500/15 text-blue-300", bar: "bg-blue-500" },
  waiting: { label: "La aprobare", cls: "bg-amber-500/15 text-amber-300", bar: "bg-amber-500" },
  returned: { label: "Întors", cls: "bg-orange-500/15 text-orange-300", bar: "bg-orange-500" },
  done: { label: "Finalizat", cls: "bg-emerald-500/15 text-emerald-300", bar: "bg-emerald-500" },
  skipped: { label: "Omis", cls: "bg-white/5 text-muted-foreground", bar: "bg-slate-700" },
};
const fmt = (iso) => (iso ? new Date(iso).toLocaleDateString("ro-RO", { day: "2-digit", month: "short", year: "numeric" }) : "—");
const uid = () => `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
const dayMs = 86400000;

function Gantt({ projectId }) {
  const [data, setData] = useState(null);
  useEffect(() => { api.get(`/projects/${projectId}/workflow/gantt`).then((r) => setData(r.data)).catch(() => {}); }, [projectId]);
  if (!data) return <p className="p-4 text-sm text-muted-foreground">Se încarcă cronologia…</p>;
  const tasks = data.tasks || [];
  if (tasks.length === 0) return <p className="p-4 text-sm text-muted-foreground">Fără activități de afișat.</p>;
  const min = new Date(tasks.reduce((a, t) => (t.start < a ? t.start : a), tasks[0].start));
  const max = new Date(tasks.reduce((a, t) => (t.end > a ? t.end : a), tasks[0].end));
  const total = Math.max(1, Math.round((max - min) / dayMs) + 1);
  const holidaySet = new Set(data.holidays || []);
  // markere lunare
  const months = [];
  let cur = new Date(min.getFullYear(), min.getMonth(), 1);
  while (cur <= max) { months.push(new Date(cur)); cur = new Date(cur.getFullYear(), cur.getMonth() + 1, 1); }

  return (
    <div className="rounded-xl border border-border bg-card p-4" data-testid="workflow-gantt">
      <div className="mb-2 flex items-center justify-between">
        <span className="text-xs text-muted-foreground">Cronologie (zile lucrătoare, calendar {data.country || "RO"} — ține cont de sărbători și concedii)</span>
      </div>
      <div className="relative ml-40 mb-1 h-4 text-[10px] text-muted-foreground">
        {months.map((m, i) => {
          const left = ((new Date(m.getFullYear(), m.getMonth(), 1) - min) / dayMs / total) * 100;
          return <span key={i} className="absolute -translate-x-1/2" style={{ left: `${Math.max(0, left)}%` }}>{m.toLocaleDateString("ro-RO", { month: "short", year: "2-digit" })}</span>;
        })}
      </div>
      <div className="space-y-1.5">
        {tasks.map((t) => {
          const s = new Date(t.start), e = new Date(t.end);
          const left = ((s - min) / dayMs / total) * 100;
          const width = Math.max(1.5, ((e - s) / dayMs + 1) / total * 100);
          const overdue = t.run_status !== "done" && e < new Date();
          const meta = STATUS_META[t.run_status] || STATUS_META.pending;
          const barCls = overdue ? "bg-red-500" : (t.over_deadline ? "bg-red-400" : meta.bar);
          const ring = (!overdue && t.over_deadline) ? " ring-2 ring-red-500/70" : "";
          return (
            <div key={t.id} className="flex items-center gap-2" data-testid={`gantt-row-${t.code || t.id}`}>
              <div className={`w-40 shrink-0 truncate text-xs ${overdue || t.over_deadline ? "text-red-400 font-semibold" : ""}`} title={t.name}>{t.code ? `${t.code} · ` : ""}{t.name}</div>
              <div className="relative h-5 flex-1 rounded bg-white/[0.03]">
                <div className={`absolute top-0 h-5 rounded ${barCls}${ring} opacity-80`} style={{ left: `${left}%`, width: `${width}%` }} title={`${t.start} → ${t.end}${overdue ? " · ÎNTÂRZIAT" : t.over_deadline ? " · NU ÎNCAPE ÎN TERMEN" : ""}`} />
              </div>
            </div>
          );
        })}
      </div>
      <div className="mt-3 flex flex-wrap gap-3 text-[11px] text-muted-foreground">
        {Object.entries(STATUS_META).filter(([k]) => ["pending", "in_progress", "waiting", "done"].includes(k)).map(([k, m]) => (
          <span key={k} className="flex items-center gap-1"><span className={`h-2.5 w-2.5 rounded ${m.bar}`} /> {m.label}</span>
        ))}
        <span>· {holidaySet.size} sărbători în interval</span>
      </div>
    </div>
  );
}

function EditMode({ projectId, inst, onSaved }) {
  const [nodes, setNodes] = useState(inst.nodes || []);
  const set = (i, k, v) => setNodes((ns) => ns.map((n, j) => (j === i ? { ...n, [k]: v } : n)));
  const setSla = (i, v) => setNodes((ns) => ns.map((n, j) => (j === i ? { ...n, sla: { ...(n.sla || {}), duration: Number(v) || 0 } } : n)));
  const del = (i) => setNodes((ns) => ns.filter((_, j) => j !== i));
  const add = () => setNodes((ns) => [...ns, { id: uid(), node_type: "activity", code: "", name: "Activitate excepție", order: ns.length, assignees: [], assignment_policy: "ANY_ONE", required_docs: [], routes: [], sla: { duration: 3, unit: "business_days", calendar_country: "RO", buffer_days: 0, reminder_hours: 24 }, escalation: { after_hours: 48 }, required: true, run_status: "pending" }]);
  const save = async () => {
    try { await api.put(`/projects/${projectId}/workflow`, { nodes: nodes.map((n, i) => ({ ...n, order: i })) }); toast.success("Excepție salvată pentru acest proiect"); onSaved(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  return (
    <div className="rounded-xl border border-amber-500/25 bg-amber-500/[0.04] p-4" data-testid="wf-edit-mode">
      <p className="mb-3 text-xs text-amber-300">Modifici workflow-ul DOAR pentru acest proiect (excepție). Nu afectează șablonul principal.</p>
      <div className="space-y-2">
        {nodes.map((n, i) => (
          <div key={n.id} className="flex flex-wrap items-center gap-2 rounded-lg border border-border/60 p-2" data-testid={`wf-edit-node-${i}`}>
            <Input value={n.code || ""} onChange={(e) => set(i, "code", e.target.value)} className="h-8 w-20 text-center text-xs" placeholder="Cod" />
            <Input value={n.name} onChange={(e) => set(i, "name", e.target.value)} className="h-8 flex-1 text-sm" data-testid={`wf-edit-name-${i}`} />
            <div className="flex items-center gap-1"><span className="text-[10px] uppercase text-muted-foreground">Termen</span><Input type="number" value={n.sla?.duration ?? 3} onChange={(e) => setSla(i, e.target.value)} className="h-8 w-16 text-xs" /><span className="text-[10px] text-muted-foreground">zile</span></div>
            <Button variant="ghost" size="icon" className="h-8 w-8 text-red-400" onClick={() => del(i)} data-testid={`wf-edit-del-${i}`}><Trash size={14} /></Button>
          </div>
        ))}
      </div>
      <div className="mt-3 flex gap-2">
        <Button variant="outline" size="sm" onClick={add} data-testid="wf-edit-add"><Plus size={14} className="mr-1" /> Adaugă activitate</Button>
        <Button size="sm" onClick={save} data-testid="wf-edit-save"><FloppyDisk size={14} className="mr-1" /> Salvează excepția</Button>
      </div>
    </div>
  );
}

export default function WorkflowPanel({ projectId, highlightHint = null }) {
  const { hasRole } = useAuth();
  const [inst, setInst] = useState(null);
  const [hlNode, setHlNode] = useState(null);
  const hlTimer = useRef(null);
  const [users, setUsers] = useState([]);
  const [transfers, setTransfers] = useState([]);
  const [atts, setAtts] = useState([]);
  const [folderDocs, setFolderDocs] = useState([]);
  const [preview, setPreview] = useState(null); // {url, filename, kind}
  const [dlg, setDlg] = useState(null);
  const [view, setView] = useState("list"); // list | gantt | edit
  const [form, setForm] = useState({ kind: "forward", action: "execute", assignee: { kind: "department", ref: "" }, documents: "", message: "" });

  const nameOf = (id) => users.find((u) => u.id === id)?.name || "";
  const fileUrl = (p) => `${process.env.REACT_APP_BACKEND_URL}/api/files/${p}?auth=${localStorage.getItem("hgs_token")}`;
  const load = () => {
    api.get(`/projects/${projectId}/workflow`).then((r) => setInst(r.data)).catch(() => {});
    api.get(`/projects/${projectId}/workflow/transfers`).then((r) => setTransfers(r.data)).catch(() => {});
    api.get(`/projects/${projectId}/workflow/attachments`).then((r) => setAtts(r.data)).catch(() => {});
    api.get(`/projects/${projectId}/folder`).then((r) => setFolderDocs((r.data || []).filter((f) => f.category !== "email"))).catch(() => {});
  };
  useEffect(() => { load(); api.get("/users").then((r) => setUsers(r.data)).catch(() => {}); /* eslint-disable-next-line */ }, [projectId]);

  // Evidențiază nodul de workflow corespunzător etapei-activitate alese din linia de flux.
  useEffect(() => {
    if (!highlightHint || !inst) return;
    const nodes = inst.nodes || [];
    const norm = (s) => (s || "").toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");
    const kws = norm(`${highlightHint.label || ""} ${highlightHint.actor || ""}`).split(/[^a-z0-9]+/).filter((w) => w.length >= 4);
    const match = nodes.find((n) => kws.some((k) => norm(n.name).includes(k)));
    if (match) {
      setView("list");
      const nid = match.code || match.id;
      setHlNode(nid);
      setTimeout(() => { const el = document.querySelector(`[data-testid="wf-inst-node-${nid}"]`); if (el) el.scrollIntoView({ behavior: "smooth", block: "center" }); }, 200);
      clearTimeout(hlTimer.current);
      hlTimer.current = setTimeout(() => setHlNode(null), 4000);
    }
    // eslint-disable-next-line
  }, [highlightHint, inst]);

  const delFolderDoc = async (fid) => {
    if (!window.confirm("Ștergi documentul din dosarul digital?")) return;
    try { await api.delete(`/projects/${projectId}/folder/${fid}`); toast.success("Document șters din dosar"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const addDocToTransfer = (name) => setForm((f) => {
    const list = f.documents.split(",").map((x) => x.trim()).filter(Boolean);
    if (list.includes(name)) return f;
    return { ...f, documents: [...list, name].join(", ") };
  });

  const uploadAtt = async (nodeId, file) => {
    if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    try { await api.post(`/projects/${projectId}/workflow/nodes/${nodeId}/attachments`, fd); toast.success("Fișier atașat"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const delAtt = async (aid) => { try { await api.delete(`/projects/${projectId}/workflow/attachments/${aid}`); load(); } catch { toast.error("Eroare la ștergere"); } };
  const kindOf = (name, ct) => {
    const n = (name || "").toLowerCase(); ct = ct || "";
    if (ct.startsWith("image/") || /\.(png|jpe?g|webp|gif|bmp|svg)$/.test(n)) return "image";
    if (ct === "application/pdf" || n.endsWith(".pdf")) return "pdf";
    return "other";
  };
  const openFile = (path, filename, ct) => {
    const url = fileUrl(path); const k = kindOf(filename, ct);
    if (k === "other") { window.open(url, "_blank"); return; }
    setPreview({ url, filename, kind: k });
  };

  const regenerate = async () => { try { await api.post(`/projects/${projectId}/workflow/regenerate`); toast.success("Workflow regenerat din șablonul principal"); load(); } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); } };
  const complete = async (nodeId) => { try { await api.post(`/projects/${projectId}/workflow/nodes/${nodeId}/complete`, {}); toast.success("Activitate finalizată"); load(); } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); } };
  const submitTransfer = async () => {
    try {
      await api.post(`/projects/${projectId}/workflow/nodes/${dlg.id}/transfer`, {
        kind: form.kind, action: form.action,
        to: form.assignee.ref ? [{ ...form.assignee, name: form.assignee.ref }] : [],
        documents: form.documents.split(",").map((x) => x.trim()).filter(Boolean), message: form.message,
      });
      toast.success("Transmis (ping-pong)");
      setDlg(null); setForm({ kind: "forward", action: "execute", assignee: { kind: "department", ref: "" }, documents: "", message: "" });
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  if (!inst) return <div className="rounded-xl border border-border bg-card p-6 text-sm text-muted-foreground">Se încarcă workflow-ul…</div>;
  const nodes = inst.nodes || [];
  const opts = form.assignee.kind === "user" ? users.map((u) => ({ value: u.id, label: `${u.name} · ${u.department || u.role}` }))
    : form.assignee.kind === "department" ? DEPARTMENTS : ROLES;
  const canEdit = hasRole("admin", "pm");

  return (
    <div className="space-y-4" data-testid="workflow-panel">
      <div className="flex flex-wrap items-center justify-between gap-2 rounded-xl border border-border bg-card p-4">
        <div>
          <div className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Șablon aplicat</div>
          <div className="font-head text-lg font-bold">{inst.template_name || "—"} <span className="text-sm font-normal text-muted-foreground">v{inst.template_version}{inst.modified ? " · ajustat pt. proiect" : ""}</span></div>
        </div>
        <div className="flex flex-wrap gap-1.5">
          <div className="flex rounded-lg border border-border p-0.5">
            <button onClick={() => setView("list")} className={`flex items-center gap-1 rounded px-2.5 py-1 text-xs ${view === "list" ? "bg-primary/15 text-primary" : "text-muted-foreground"}`} data-testid="wf-view-list"><ListBullets size={14} /> Listă</button>
            <button onClick={() => setView("gantt")} className={`flex items-center gap-1 rounded px-2.5 py-1 text-xs ${view === "gantt" ? "bg-primary/15 text-primary" : "text-muted-foreground"}`} data-testid="wf-view-gantt"><ChartBar size={14} /> Gantt</button>
            {canEdit && <button onClick={() => setView("edit")} className={`flex items-center gap-1 rounded px-2.5 py-1 text-xs ${view === "edit" ? "bg-amber-500/15 text-amber-300" : "text-muted-foreground"}`} data-testid="wf-view-edit"><PencilSimple size={14} /> Editare</button>}
          </div>
          {canEdit && <Button variant="outline" size="sm" onClick={regenerate} data-testid="wf-regenerate-btn"><ArrowsClockwise size={15} className="mr-1.5" /> Regenerează</Button>}
        </div>
      </div>

      {view === "gantt" && <Gantt projectId={projectId} />}
      {view === "edit" && <EditMode projectId={projectId} inst={inst} onSaved={load} />}
      {view === "list" && (
        <div className="space-y-2">
          <div className="rounded-xl border border-border bg-card p-4" data-testid="wf-folder-docs">
            <div className="mb-2 flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider text-muted-foreground"><Paperclip size={13} /> Documente din dosarul digital ({folderDocs.length})</div>
            {folderDocs.length === 0 ? (
              <p className="text-xs text-muted-foreground">Niciun document în dosar. Adaugă documente în tab-ul „Dosar".</p>
            ) : (
              <div className="flex flex-wrap gap-1.5">
                {folderDocs.map((d) => (
                  <span key={d.id} className="flex items-center gap-1 rounded border border-border bg-white/[0.02] px-2 py-1 text-xs" data-testid={`wf-folderdoc-${d.id}`}>
                    <button onClick={() => openFile(d.storage_path, d.name, d.content_type)} className="max-w-[200px] truncate text-primary hover:underline" title={d.name}>{d.name}</button>
                    <button onClick={() => addDocToTransfer(d.name)} className="text-muted-foreground hover:text-emerald-400" title="Include la transmitere" data-testid={`wf-folderdoc-add-${d.id}`}><PaperPlaneTilt size={12} /></button>
                    {hasRole("admin", "pm", "inginer", "vanzari") && <button onClick={() => delFolderDoc(d.id)} className="text-muted-foreground hover:text-red-400" title="Șterge din dosar" data-testid={`wf-folderdoc-del-${d.id}`}><Trash size={11} /></button>}
                  </span>
                ))}
              </div>
            )}
          </div>
          {nodes.map((n) => {
            const st = STATUS_META[n.run_status] || STATUS_META.pending;
            return (
              <div key={n.id} className={`rounded-xl border bg-card p-4 transition-all ${hlNode === (n.code || n.id) ? "border-primary ring-2 ring-primary/60" : "border-border"}`} data-testid={`wf-inst-node-${n.code || n.id}`}>
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      {n.code && <span className="rounded bg-white/5 px-2 py-0.5 font-mono text-xs">{n.code}</span>}
                      <span className="font-semibold">{n.name}</span>
                      <span className={`rounded px-2 py-0.5 text-[11px] font-bold ${st.cls}`}>{st.label}</span>
                    </div>
                    <div className="mt-1.5 flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
                      {n.current_holder_id && <span>Responsabil curent: <b className="text-foreground">{nameOf(n.current_holder_id)}</b></span>}
                      {n.due_at && <span className="flex items-center gap-1"><ClockCountdown size={13} /> Termen: {fmt(n.due_at)}</span>}
                      {(n.required_docs || []).length > 0 && <span>Docs: {n.required_docs.join(", ")}</span>}
                    </div>
                  </div>
                  <div className="flex gap-1.5">
                    <Button variant="outline" size="sm" onClick={() => setDlg(n)} data-testid={`wf-transfer-btn-${n.code || n.id}`}><PaperPlaneTilt size={14} className="mr-1" /> Transmite</Button>
                    {n.run_status !== "done" && <Button variant="ghost" size="sm" className="text-emerald-400" onClick={() => complete(n.id)} data-testid={`wf-complete-btn-${n.code || n.id}`}><CheckCircle size={16} /></Button>}
                  </div>
                </div>
                <div className="mt-2 rounded-md border border-border/60 bg-white/[0.02] p-2" data-testid={`wf-att-block-${n.code || n.id}`}>
                  {(n.template_docs || []).length > 0 && (
                    <div className="mb-2">
                      <div className="mb-1 flex items-center gap-1.5 text-[10px] font-bold uppercase text-muted-foreground"><Paperclip size={12} /> Documente-model</div>
                      <div className="flex flex-wrap items-center gap-1.5">
                        {(n.template_docs || []).map((d) => (
                          <button key={d.id} onClick={() => openFile(d.storage_path, d.filename, d.content_type)} className="flex items-center gap-1 rounded border border-primary/30 bg-primary/[0.06] px-2 py-1 text-xs text-primary hover:bg-primary/10" data-testid={`wf-modeldoc-${d.id}`} title={d.filename}>
                            <span className="max-w-[180px] truncate">{d.filename}</span>
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                  <div className="mb-1 flex items-center gap-1.5 text-[10px] font-bold uppercase text-muted-foreground"><Paperclip size={12} /> Atașamente</div>
                  <div className="flex flex-wrap items-center gap-1.5">
                    {atts.filter((a) => a.node_id === n.id).map((a) => (
                      <span key={a.id} className="flex items-center gap-1 rounded border border-border bg-card px-2 py-1 text-xs" data-testid={`wf-att-${a.id}`}>
                        <button onClick={() => openFile(a.storage_path, a.filename, a.content_type)} className="max-w-[180px] truncate text-primary hover:underline" title={a.filename}>{a.filename}</button>
                        <button onClick={() => delAtt(a.id)} className="text-muted-foreground hover:text-red-400" data-testid={`wf-att-del-${a.id}`}><Trash size={11} /></button>
                      </span>
                    ))}
                    {atts.filter((a) => a.node_id === n.id).length === 0 && <span className="text-xs text-muted-foreground">Niciun fișier atașat.</span>}
                    <label className="flex cursor-pointer items-center gap-1 rounded border border-dashed border-border px-2 py-1 text-xs text-muted-foreground transition-colors hover:border-primary hover:text-primary" data-testid={`wf-att-upload-${n.code || n.id}`}>
                      <Plus size={12} /> Adaugă fișier
                      <input type="file" className="hidden" onChange={(e) => { uploadAtt(n.id, e.target.files?.[0]); e.target.value = ""; }} />
                    </label>
                  </div>
                </div>
              </div>
            );
          })}
          {nodes.length === 0 && <p className="rounded-xl border border-border bg-card p-6 text-center text-sm text-muted-foreground">Fără workflow. Directorul trebuie să publice un șablon principal.</p>}
        </div>
      )}

      <div className="rounded-xl border border-border bg-card p-4">
        <h4 className="mb-3 font-head text-base font-bold">Istoric transmiteri (ping-pong)</h4>
        <div className="space-y-1.5">
          {transfers.map((t) => (
            <div key={t.id} className="flex flex-wrap items-center gap-2 rounded-lg border border-border/60 px-3 py-2 text-xs">
              <span className={`rounded px-1.5 py-0.5 font-bold ${t.kind === "return" ? "bg-orange-500/15 text-orange-300" : t.kind === "complete" ? "bg-emerald-500/15 text-emerald-300" : "bg-blue-500/15 text-blue-300"}`}>
                {t.kind === "return" ? "Întors" : t.kind === "complete" ? "Finalizat" : t.kind === "clarify" ? "Clarificare" : "Transmis"}
              </span>
              <span className="font-medium text-foreground">{t.node_name}</span>
              <span className="text-muted-foreground">{t.from_user_name} → {(t.to || []).map((a) => a.name || a.ref).join(", ") || "—"}</span>
              {t.message && <span className="text-muted-foreground">· „{t.message}"</span>}
              {(t.attachments || []).length > 0 && <span className="flex items-center gap-0.5 text-emerald-400" title={(t.attachments || []).map((a) => a.filename).join(", ")}><Paperclip size={11} /> {t.attachments.length}</span>}
              <span className="ml-auto text-muted-foreground">{fmt(t.created_at)}</span>
            </div>
          ))}
          {transfers.length === 0 && <p className="text-sm text-muted-foreground">Nicio transmitere încă.</p>}
        </div>
      </div>

      <Dialog open={!!dlg} onOpenChange={(o) => !o && setDlg(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Transmite: {dlg?.name}</DialogTitle></DialogHeader>
          <div className="grid gap-3">
            <div className="grid grid-cols-2 gap-3">
              <div className="grid gap-1.5">
                <label className="text-xs font-bold uppercase text-muted-foreground">Tip</label>
                <Select value={form.kind} onValueChange={(v) => setForm({ ...form, kind: v })}>
                  <SelectTrigger data-testid="wf-transfer-kind"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="forward">Înainte</SelectItem>
                    <SelectItem value="return">Întoarce (ping-pong)</SelectItem>
                    <SelectItem value="clarify">Cere clarificare</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="grid gap-1.5">
                <label className="text-xs font-bold uppercase text-muted-foreground">Acțiune</label>
                <Select value={form.action} onValueChange={(v) => setForm({ ...form, action: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{["execute", "review", "approve", "sign", "clarify"].map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid gap-1.5">
              <label className="text-xs font-bold uppercase text-muted-foreground">Către</label>
              <div className="flex gap-2">
                <Select value={form.assignee.kind} onValueChange={(k) => setForm({ ...form, assignee: { kind: k, ref: "" } })}>
                  <SelectTrigger className="w-36" data-testid="wf-transfer-kind-to"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="role">Rol</SelectItem>
                    <SelectItem value="department">Departament</SelectItem>
                    <SelectItem value="user">Persoană</SelectItem>
                  </SelectContent>
                </Select>
                <Select value={form.assignee.ref} onValueChange={(r) => setForm({ ...form, assignee: { ...form.assignee, ref: r } })}>
                  <SelectTrigger className="flex-1" data-testid="wf-transfer-to"><SelectValue placeholder="Alege…" /></SelectTrigger>
                  <SelectContent>{opts.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid gap-1.5">
              <label className="text-xs font-bold uppercase text-muted-foreground">Documente de transmis (virgulă)</label>
              <Input value={form.documents} onChange={(e) => setForm({ ...form, documents: e.target.value })} placeholder="ex: Proiect tehnic Rev.0, BOM" data-testid="wf-transfer-docs" />
              {folderDocs.length > 0 && (
                <div className="mt-1">
                  <div className="mb-1 text-[10px] uppercase text-muted-foreground">Alege din dosar:</div>
                  <div className="flex flex-wrap gap-1.5">
                    {folderDocs.map((d) => (
                      <button key={d.id} type="button" onClick={() => addDocToTransfer(d.name)} className="max-w-[200px] truncate rounded border border-primary/30 bg-primary/[0.06] px-2 py-0.5 text-[11px] text-primary hover:bg-primary/10" title={d.name} data-testid={`wf-transfer-pick-${d.id}`}>+ {d.name}</button>
                    ))}
                  </div>
                </div>
              )}
            </div>
            <div className="grid gap-1.5">
              <label className="text-xs font-bold uppercase text-muted-foreground">Mesaj</label>
              <Textarea value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} data-testid="wf-transfer-message" />
            </div>
          </div>
          <DialogFooter><Button onClick={submitTransfer} data-testid="wf-transfer-submit"><PaperPlaneTilt size={15} className="mr-1.5" /> Transmite</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!preview} onOpenChange={(o) => !o && setPreview(null)}>
        <DialogContent className="max-w-4xl" data-testid="wf-preview-dialog">
          <DialogHeader><DialogTitle className="truncate pr-6">{preview?.filename}</DialogTitle></DialogHeader>
          {preview?.kind === "image" ? (
            <img src={preview.url} alt={preview.filename} className="mx-auto max-h-[75vh] rounded" />
          ) : preview?.kind === "pdf" ? (
            <iframe title="preview" src={preview.url} className="h-[75vh] w-full rounded border border-border" />
          ) : null}
          <DialogFooter>
            <a href={preview?.url} target="_blank" rel="noreferrer"><Button variant="outline"><DownloadSimple size={15} className="mr-1.5" /> Descarcă</Button></a>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
